import { ChevronDown, ChevronRight } from '@mynaui/icons-react';
import clsx from 'clsx';
import { Fragment, FunctionComponent, useId, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { OfflineIndicator } from './offline/OfflineIndicator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

/** Extract the project id from a `/projects/:id` step href (project crumb only). */
const projectIdFromHref = (href: string): string | null => {
  const m = /^\/projects\/([^/?#]+)$/.exec(href);
  return m ? m[1] : null;
};

interface iBreadcrumbsProps {
  steps: { label: string; href: string; options?: { label: string; href: string; onPrefetch?: () => void }[] }[];
}

const Breadcrumbs: FunctionComponent<iBreadcrumbsProps> = ({ steps }) => {
  const id = useId();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const prefetchTimeout = useRef<ReturnType<typeof setTimeout>>();

  return (
    <div className='flex items-center gap-2 text-sm text-slate-700/70 dark:text-stone-100/70'>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;

        const href = params.toString() ? `${step.href}?${params.toString()}` : step.href;
        const projectId = projectIdFromHref(step.href);
        return (
          <Fragment key={`${id}.${step.href}.${index}`}>
            <Link
              className={clsx(
                'hover:text-slate-700 dark:hover:text-stone-100 leading-tight inline-flex items-center gap-1.5',
                isLast && 'text-slate-700 dark:text-stone-100',
              )}
              to={href}>
              {step.label}
              {projectId && <OfflineIndicator projectId={projectId} />}
            </Link>
            {step.options && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className='flex items-center gap-2 cursor-pointer'>
                    <ChevronDown className='size-3.5 stroke-2 text-slate-500 dark:text-stone-100' />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='w-56' align='start'>
                  {step.options.map((option) => (
                    <DropdownMenuItem
                      key={option.href}
                      onClick={() => navigate(option.href)}
                      onMouseEnter={() => {
                        clearTimeout(prefetchTimeout.current);
                        if (option.onPrefetch) {
                          prefetchTimeout.current = setTimeout(option.onPrefetch, 50);
                        }
                      }}
                      onMouseLeave={() => clearTimeout(prefetchTimeout.current)}>
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!isLast && <ChevronRight className='size-3.5 stroke-2 text-slate-500 dark:text-stone-100' />}
          </Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumbs;
